import React, { useId, useState, type ComponentProps, type ReactNode } from 'react';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import { Popup } from '@atlaskit/popup/popup';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';

import { CreditsSurface } from '../../internal/credits-surface';
import type { CreditsBannerProps } from '../credits-banner';

const styles = cssMap({
	content: {
		boxSizing: 'border-box',
		width: '248px',
		minHeight: '116px',
		maxWidth: '100%',
		textAlign: 'left',
	},
});

export type CreditsPopupTriggerProps = Parameters<ComponentProps<typeof Popup>['trigger']>[0] & {
	id: string;
	isSelected: boolean;
	onClick: () => void;
};

export type CreditsPopupProps = Omit<CreditsBannerProps, 'xcss'> & {
	/** Optional localized heading displayed inside the popup. */
	title?: string;
	/** Render a custom button, forwarding all supplied props (including ref) to it. */
	trigger?: (props: CreditsPopupTriggerProps) => ReactNode;
	/** Popup position relative to the trigger. Defaults to `bottom-start`. */
	placement?: ComponentProps<typeof Popup>['placement'];
	/** Popup `[skidding, distance]` offset from the trigger, in pixels. */
	offset?: ComponentProps<typeof Popup>['offset'];
	/**
	 * Render the popup next to its trigger instead of in a portal. Defaults to `true`.
	 * Set to `false` inside scrolling containers such as menus, which otherwise clip the popup and
	 * flip it to the opposite side.
	 */
	shouldRenderToParent?: boolean;
};

const CreditsPopupContent = ({
	title,
	triggerLabel,
	trigger,
	details,
	placement = 'bottom-start',
	offset,
	shouldRenderToParent = true,
}: {
	title?: string;
	triggerLabel: string;
	trigger?: CreditsPopupProps['trigger'];
	details: ReactNode;
	placement?: CreditsPopupProps['placement'];
	offset?: CreditsPopupProps['offset'];
	shouldRenderToParent?: boolean;
}): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);
	const id = useId();
	const titleId = useId();
	const triggerId = useId();
	return (
		<Popup
			id={id}
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			role="dialog"
			titleId={title ? titleId : triggerId}
			placement={placement}
			offset={offset}
			shouldFitViewport
			shouldRenderToParent={shouldRenderToParent}
			// Hosts such as menus may stop click propagation, which would hide outside clicks.
			shouldUseCaptureOnOutsideClick
			content={() => (
				<Box paddingBlock="space.250" paddingInline="space.400" xcss={styles.content}>
					<Stack space="space.100">
						{title && (
							<Heading size="small" as="h2" id={titleId}>
								{title}
							</Heading>
						)}
						{details}
					</Stack>
				</Box>
			)}
			trigger={(triggerProps) => {
				const buttonProps: CreditsPopupTriggerProps = {
					...triggerProps,
					id: triggerId,
					isSelected: isOpen,
					onClick: () => setIsOpen((open) => !open),
				};
				return trigger ? (
					trigger(buttonProps)
				) : (
					<IconButton
						{...buttonProps}
						appearance="subtle"
						icon={(iconProps) => <InformationCircleIcon {...iconProps} size="medium" />}
						label={triggerLabel}
					/>
				);
			}}
		/>
	);
};

export const CreditsPopup = ({
	title,
	trigger,
	placement,
	offset,
	shouldRenderToParent,
	...props
}: CreditsPopupProps): React.JSX.Element => (
	<CreditsSurface {...props}>
		{({ title: triggerLabel, description }) => (
			<CreditsPopupContent
				title={title}
				trigger={trigger}
				placement={placement}
				offset={offset}
				shouldRenderToParent={shouldRenderToParent}
				triggerLabel={triggerLabel}
				details={description}
			/>
		)}
	</CreditsSurface>
);
